import React, { createContext, useCallback, useContext, useState } from "react";

export type NotificationType = "success" | "error" | "info";

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  timeout?: number;
}

interface NotificationContextValue {
  notifications: Notification[];
  notify: (n: Omit<Notification, "id">) => string;
  remove: (id: string) => void;
  success: (message: string, timeout?: number) => string;
  error: (message: string, timeout?: number) => string;
  info: (message: string, timeout?: number) => string;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const remove = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback((n: Omit<Notification, "id">) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const notification: Notification = { id, ...n };
    setNotifications((prev) => [...prev, notification]);

    const timeout = n.timeout ?? (n.type === "error" ? 7000 : 5000);
    if (timeout && timeout > 0) {
      setTimeout(() => remove(id), timeout);
    }

    return id;
  }, [remove]);

  const success = useCallback((message: string, timeout?: number) => notify({ type: "success", message, timeout }), [notify]);
  const error = useCallback((message: string, timeout?: number) => notify({ type: "error", message, timeout }), [notify]);
  const info = useCallback((message: string, timeout?: number) => notify({ type: "info", message, timeout }), [notify]);

  return (
    <NotificationContext.Provider value={{ notifications, notify, remove, success, error, info }}>
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
